import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Globe, Truck, DollarSign, Clock, CheckCircle, Package, TrendingUp, MapPin, Anchor } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
function fmtAmt(v) { return v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`; }

export default function AdminCorridor() {
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/admin/corridor-analytics`, { withCredentials: true }); setData(d); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!data) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-[#22C55E] border-t-transparent rounded-full animate-spin" /></div>;

  const maxMonthly = Math.max(...data.monthly_volumes.map(m => m.volume));

  return (
    <div data-testid="admin-corridor">
      <div className="mb-6">
        <h1 className="font-['Outfit'] text-2xl font-bold text-white">{data.corridor}</h1>
        <p className="text-[#A1B2A8]/60 text-sm">{data.period} &middot; Corridor Intelligence</p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
        {[
          [DollarSign, "Trade Volume", fmtAmt(data.total_volume), "text-[#22C55E]"],
          [Package, "Shipments", data.total_shipments, "text-white"],
          [TrendingUp, "Avg Deal", fmtAmt(data.avg_deal_size), "text-blue-400"],
          [Clock, "Avg Transit", `${data.avg_transit_days}d`, "text-yellow-400"],
          [CheckCircle, "On-Time", `${data.on_time_rate}%`, "text-[#22C55E]"],
          [Truck, "Settlement Cycle", `${data.settlement_cycle_days}d`, "text-purple-400"],
        ].map(([Icon, label, value, color]) => (
          <div key={label} className="bg-[#09120D] border border-[#1A2E22] p-5">
            <div className="flex items-center gap-2 mb-2"><Icon className={`w-4 h-4 ${color}`} /><span className="text-[#A1B2A8] text-xs">{label}</span></div>
            <span className={`font-['Outfit'] text-2xl font-bold ${color}`}>{value}</span>
          </div>
        ))}
      </div>

      {/* Monthly Volume Chart */}
      <div className="bg-[#09120D] border border-[#1A2E22] p-6 mb-6">
        <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-5 uppercase tracking-wider">Monthly Trade Volume</h2>
        <div className="flex items-end gap-3 h-40">
          {data.monthly_volumes.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
              <span className="text-[#22C55E] text-xs font-mono">{fmtAmt(m.volume)}</span>
              <div className="w-full bg-[#22C55E]/20 relative" style={{ height: `${(m.volume / maxMonthly) * 100}%`, minHeight: "8px" }}>
                <div className="absolute inset-0 bg-[#22C55E]/60" />
              </div>
              <div className="text-center">
                <span className="text-[#A1B2A8]/60 text-[10px] block">{m.month.split(" ")[0]}</span>
                <span className="text-[#A1B2A8]/30 text-[10px] block">{m.shipments} shp</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Top Products */}
        <div className="bg-[#09120D] border border-[#1A2E22] p-6">
          <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider">Top Products</h2>
          <div className="space-y-3">
            {data.top_products.map((p) => (
              <div key={p.product}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white text-sm">{p.product}</span>
                  <span className="text-[#A1B2A8] text-xs">{fmtAmt(p.volume)} ({p.share}%)</span>
                </div>
                <div className="w-full h-1.5 bg-[#1A2E22] rounded-full overflow-hidden">
                  <div className="h-full bg-[#22C55E]/60 rounded-full transition-all" style={{ width: `${p.share}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance */}
        <div className="bg-[#09120D] border border-[#1A2E22] p-6">
          <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider">Corridor Performance</h2>
          <div className="space-y-3">
            {[
              ["Container Utilization", `${data.avg_container_utilization}%`, data.avg_container_utilization >= 85 ? "text-[#22C55E]" : "text-yellow-400"],
              ["Quality Rejection Rate", `${data.quality_rejection_rate}%`, data.quality_rejection_rate <= 3 ? "text-[#22C55E]" : "text-red-400"],
              ["On-Time Delivery", `${data.on_time_rate}%`, data.on_time_rate >= 85 ? "text-[#22C55E]" : "text-yellow-400"],
              ["Avg Settlement Cycle", `${data.settlement_cycle_days} days`, data.settlement_cycle_days <= 21 ? "text-[#22C55E]" : "text-yellow-400"],
              ["Active Exporters", data.active_exporters, "text-white"],
              ["Active Buyers", data.active_buyers, "text-white"],
            ].map(([label, val, color]) => (
              <div key={label} className="flex items-center justify-between border-b border-[#1A2E22] pb-2 last:border-0 last:pb-0">
                <span className="text-[#A1B2A8] text-sm">{label}</span>
                <span className={`font-['Outfit'] font-semibold ${color}`}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ports */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#09120D] border border-[#1A2E22] p-6">
          <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
            <Anchor className="w-4 h-4 text-[#22C55E]" /> Export Ports (India)
          </h2>
          <div className="space-y-3">
            {data.top_export_ports.map((p) => (
              <div key={p.port} className="flex items-center justify-between bg-[#050A07] border border-[#1A2E22] p-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#22C55E]" />
                  <div><p className="text-white text-sm">{p.port}</p><p className="text-[#A1B2A8]/40 text-xs">{p.shipments} shipments</p></div>
                </div>
                <span className="text-[#22C55E] font-['Outfit'] font-semibold text-sm">{fmtAmt(p.volume)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-6">
          <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-400" /> Import Ports (USA)
          </h2>
          <div className="space-y-3">
            {data.top_import_ports.map((p) => (
              <div key={p.port} className="flex items-center justify-between bg-[#050A07] border border-[#1A2E22] p-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-blue-400" />
                  <div><p className="text-white text-sm">{p.port}</p><p className="text-[#A1B2A8]/40 text-xs">{p.shipments} shipments</p></div>
                </div>
                <span className="text-blue-400 font-['Outfit'] font-semibold text-sm">{fmtAmt(p.volume)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
